/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import {
  Component, ElementRef, Input,
  OnChanges, OnInit,
  SimpleChanges, ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { cloneDeep, assign } from 'lodash-es';
import { ButtonStyle } from 'src/aero/aero-button/aero-button.component';
import { AeroFormComponent } from 'src/aero/aero-form/aero-form.component';
import { AeroTextareaComponent } from 'src/aero/aero-textarea/aero-textarea.component';
import { SvgIconRegistry } from '@ngneat/svg-icon';
import { bulbGripLinesSolidIcon } from 'IconLibrary/bulb/grip-lines-solid';
import { ResumeAboutMeSubSection } from './about-me-sub-section.model';
import { ResumeComponent } from '../resume.component';
import { ResumeSection } from '../section/section.model';
import { ResumeSubSectionComponent } from '../sub-section/sub-section.component';
import { ResumeSectionComponent } from '../section/section.component';
import { ResumeSectionType } from '../section/resume-section-type.enum';
import { SectionDataAlertService } from '../section/section-data-alert.service';
import { UnitSimpleDTO } from '../content-selection-modal/unit-simple-dto.model';

/**
 * Angular component that will house the 'About Me' resume section.
 */
@Component({
  selector: 'bulb-resume-about-me',
  templateUrl: './about-me.component.html',
  standalone: false,
})
export class ResumeAboutMeComponent implements OnChanges, OnInit {
  /**
   * About me section model.
   */
  @Input() aboutMeSection: ResumeSection;

  /**
   * Whether the current user can edit the section.
   */
  @Input() canEdit: boolean;

  /**
   * Reference to the parent resume component.
   */
  @Input() resumeComponent: ResumeComponent;

  /**
   *  Boolean indicating if the current accordion tab is open.
   */
  public isTabOpen: boolean = false;

  /**
   * Boolean indicating if the data is valid to save.
   */
  public isValid: boolean = false;

  /**
   * Boolean indicating if the content ( data ) has been updated.
   */
  public contentUpdated: boolean = false;

  /**
   * Whether the section is showing the edit form or not.
   */
  public editMode : boolean = false;

  /**
   * Tracks for the tab changes were made in session.
   */
  public tabChange: boolean;

  /**
   * Clone of the about me sub-section data to use during edit, so any changes can be cancelled or saved as needed.
   * @private
   */
  public aboutMeSubSectionClone : ResumeAboutMeSubSection;

  /**
   * Set the value as true if the subsection contains only portfolio content.
   */
  public isOnlyPortfolioContent : boolean = false;

  /**
   * Array of portfolio content that the user has selected.
   */
  public portfolioContent : Array< UnitSimpleDTO > = new Array<UnitSimpleDTO>();

  /**
   * Array of portfolio content that the user has removed.
   */
  public removedPortfolioContent : Array< UnitSimpleDTO > = new Array<UnitSimpleDTO>();

  /**
   * Number indicating how many units a user has selected to display in their portfolio content.
   *
   * @private
   */
  private numberOfSelectedContent : number = 0;

  /**
   * Number indicating how many units a user has removed from their portfolio content.
   */
  public numberOfRemovedContent : number = 0;

  /**
   * An Array of the initial values in the aero form component. It is an array, since other sections
   * (Experience && Education) have an aeroFormComponentList which is an array. For About Me there would only ever be
   *  one entry.
   * @private
   */
  private initialAeroFormValuesArray : Array<any> = new Array<any>();

  /**
   * Boolean indicating if the user has saved the data in this section.
   * @private
   */
  private hasSaved : boolean = false;

  /**
   * Button style of aero button
   */
  public buttonStyle: ButtonStyle = ButtonStyle.PRIMARY_2;

  /**
   * Max length of description field
   */
  public maxLength: number = 1000;

  /**
   * flag for character limit reached.
   */
  public charLimitReached: boolean = false;

  /**
   * Reference to the AeroTextareaComponent in the about me section.
   */
  @ViewChild(AeroTextareaComponent) aeroTextareaComponent: AeroTextareaComponent;

  /**
   * Reference to the AeroFormComponent in the about me section.
   */
  @ViewChild(AeroFormComponent) public aeroFormComponent: AeroFormComponent;

  /**
   * Reference to the ResumeSubSectionComponent in the about me section.
   */
  @ViewChild(ResumeSubSectionComponent) subSectionComponent: ResumeSubSectionComponent;

  /**
   * Reference to our ResumeSectionComponent.
   */
  @ViewChild(ResumeSectionComponent) sectionComponent: ResumeSectionComponent;

  /**
   * Default Constructor.
   *
   * @param elementRef
   * @param sectionDataAlertService our Angular data modal alert service.
   */
  constructor(
    private elementRef : ElementRef,
    public sectionDataAlertService : SectionDataAlertService,
    private svgRegistry: SvgIconRegistry,
  ) {
    this.svgRegistry.register([bulbGripLinesSolidIcon]);
  }

  /**
   * A callback method that is invoked immediately after the default change detector has checked the directive's
   * data-bound properties for the first time, and before any of the view or content children have been checked.
   */
  public ngOnInit(): void {
    // If the about me section has been populated, we default the accordion tab to be open.

    if (this.tabChange) {
      this.isTabOpen = JSON.parse(sessionStorage.getItem('aboutMeTabOpen'));
    } else if (!this.aboutMeSection.empty && !this.tabChange) {
      this.isTabOpen = true;
    } else {
      sessionStorage.aboutMeTabOpen = false;
    }
    this.cloneSubSection();
    this.initializeFields();
  }

  /**
   * Response handler for edit event from section component which moves us into 'edit' mode.
   */
  public beginEdit() {
    this.cloneSubSection();
    this.isTabOpen = true;
    sessionStorage.aboutMeTabOpen = true;
    this.editMode = true;
    setTimeout(() => {
      this.sectionDataAlertService.enableAlert(this.aeroFormComponent, () => { this.cancelEdit(); });
    });
  }

  /**
   * Handle closing of the accordion on the header tab click.
   *
   * @param event
   */
  handleTabClose(event : any) : void {
    this.cloneSubSection();
    this.handleUnsavedPortfolioContent();
    this.aeroFormComponent.resetAeroFormToInitialValues(this.aboutMeSubSectionClone);
    this.isTabOpen = false;
    sessionStorage.aboutMeTabOpen = false;
    this.editMode = false;
    this.sectionDataAlertService.unregisterBeforeUnloadEvent();
  }

  /**
   * Handles open of tab
   * @param event open event.
   */
  handleTabOpen(event : any) : void {
    this.isTabOpen = true;
    sessionStorage.aboutMeTabOpen = true;
  }

  /*
   * Method to clone the existing AboutMeSubSection, or create a new one if one doesn't exist.
   * @private
   */
  private cloneSubSection() {
    if (this.aboutMeSection.subSections && this.aboutMeSection.subSections[0]) {
      this.aboutMeSubSectionClone = <ResumeAboutMeSubSection>cloneDeep(this.aboutMeSection.subSections[0]);
      this.updateIsisOnlyPortfolioContent();
    } else {
      this.aboutMeSubSectionClone = new ResumeAboutMeSubSection();
    }
  }

  /**
   * Cancel button handler.
   */
  public cancelEdit() {
    if (this.aboutMeSection.empty) {
      this.isTabOpen = false;
      sessionStorage.aboutMeTabOpen = false;
    } else {
      this.editMode = false;
    }
    this.hasSaved = false;
    this.handleUnsavedPortfolioContent();
    this.resetFormState();
    this.sectionDataAlertService.unregisterBeforeUnloadEvent();
    this.sectionDataAlertService.disableAlert();
    this.cloneSubSection();
  }

  /**
   * Callback method, called when field content updated.
   * @param event
   */
  public contentChangeHandler(event) {
    this.contentUpdated = true;
  }

  /**
   * Method to save the About Me information.
   *
   * @param resumeAboutMeInfo
   */
  public save(resumeAboutMeInfo: NgForm) {
    // Update the clone with the attached portfolio content
    const portfolioContentDTO : Array< UnitSimpleDTO > = new Array< UnitSimpleDTO >();
    this.portfolioContent.forEach((unit) => {
      const unitDTO : any = {
        id: unit.id,
      };
      portfolioContentDTO.push(unitDTO);
    });
    this.aboutMeSubSectionClone.portfolioContent = portfolioContentDTO;

    // Assign the changes from the clone into the real section/subsection.
    if (this.aboutMeSection.subSections && this.aboutMeSection.subSections[0]) {
      assign(this.aboutMeSection.subSections[0], this.aboutMeSubSectionClone);
    } else {
      this.aboutMeSection.subSections = new Array<ResumeAboutMeSubSection>();
      this.aboutMeSection.subSections.push(this.aboutMeSubSectionClone);
    }

    this.aboutMeSection.empty = false;
    this.resumeComponent.save().then((updatedResume) => {
      const index = updatedResume
        .sections.findIndex((resumeSection) => resumeSection.type === ResumeSectionType.AboutMe);
      assign(this.aboutMeSection.subSections[0], updatedResume.sections[index].subSections[0]);
      this.portfolioContent = updatedResume.sections[index].subSections[0].portfolioContent;
      this.hasSaved = true;
      this.updateIsisOnlyPortfolioContent();
      resumeAboutMeInfo.resetForm(resumeAboutMeInfo.value);
      this.editMode = false;
    });
    // Update the initial value of the aero form component with the new data:
    this.initialAeroFormValuesArray[0] = resumeAboutMeInfo.form.value;
    this.resetFormState();
    this.sectionDataAlertService.unregisterBeforeUnloadEvent();
    this.sectionDataAlertService.resetPortfolioContentModifiedMap();
    this.sectionDataAlertService.disableAlert();
  }

  /**
   * Called when @Input parameter for this component is updated.
   * @param changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    this.cloneSubSection();
    this.initializeFields();
  }

  /**
   * Method to pre populate fields.
   */
  public initializeFields() {
    if (this.aeroFormComponent) {
      this.aeroFormComponent.form.resetForm(this.aeroFormComponent.form.value);
    }
    this.portfolioContent = this.aboutMeSubSectionClone.portfolioContent;
  }

  /**
   * Handle adding the user's potential portfolio content to about-me section.
   */
  public addPortfolioContent() : void {
    this.subSectionComponent.addPortfolioContent(this);
  }

  /**
   * Helper method to update the portoFolioContent object identity so that the Angular detection cycle could detect
   * the object change after the removal of any portfolio content.
   *
   * @param event
   */
  public onPortfolioContentRemoval(event : any) : void {
    this.subSectionComponent.removePortfolioContent(this);
  }

  /**
   * Method that handles removing any unsaved portfolio content from the view.
   *
   * @private
   */
  private handleUnsavedPortfolioContent() : void {
    this.subSectionComponent.handleUnsavedPortfolioContent(this);
  }

  /**
   * Check and update if the sub section contains only portfolio content.
   */
  private updateIsisOnlyPortfolioContent() {
    this.isOnlyPortfolioContent = !(this.aboutMeSubSectionClone.description);
  }

  /**
   * Callback method which is called when editor data is changed.
   * @param aboutMeData
   */
  updateAboutMeData(aboutMeData) {
    this.aboutMeSubSectionClone.description = aboutMeData;
    this.sectionDataAlertService.handleSaveOnContentUpdate(this.aeroFormComponent, this.charLimitReached);
  }

  /**
   * Callback function for character count.
   * @param charCount - current number of character in the editor.
   */
  handleLimitReached(charCount) {
    this.charLimitReached = (charCount > this.maxLength);
  }

  /**
   * Reset the form status.
   */
  resetFormState() {
    this.aeroFormComponent.markAeroFormAsPristine();
    this.aeroFormComponent.updateSaveButtonState();
  }

  /**
   *  Drag start function.
   * @param event event
   * @param section Dragged section
   */
  dragStart(event, section) {
    this.resumeComponent.dragStart(section);
  }

  /**
   * Drop function
   * @param event event
   * @param section  On dragged section
   */
  drop(event, section) {
    this.resumeComponent.drop(section);
  }
}
